import { useCallback, useEffect, useRef, useState } from 'react'
import { workOrderService } from '../services/workOrderService'
import type {
  DriverAssignmentResponse,
  WorkOrder,
  WorkOrderStatus,
} from '../types/workorder'

interface UseWorkOrdersReturn {
  workOrders: WorkOrder[]
  isLoading: boolean
  error: string | null
  createWorkOrder: (data: Omit<WorkOrder, 'id'>) => Promise<WorkOrder>
  updateWorkOrder: (id: string, data: Partial<WorkOrder>) => Promise<WorkOrder>
  updateWorkOrderStatus: (
    workOrderId: string | number,
    status: WorkOrderStatus,
    declineReason?: string,
  ) => Promise<WorkOrder>
  assignDrivers: (
    workOrderId: string | number,
    driverIds: number[],
  ) => Promise<DriverAssignmentResponse>
  refreshWorkOrders: () => Promise<void>
}

export function useWorkOrders(): UseWorkOrdersReturn {
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const hasFetchedRef = useRef(false)

  const refreshWorkOrders = useCallback(async () => {
    try {
      const data = await workOrderService.getWorkOrders({
        page: 1,
        page_size: 10,
      })
      setWorkOrders(data.items)
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to fetch work orders',
      )
    } finally {
      setIsLoading(false)
    }
  }, [])

  const createWorkOrder = async (
    data: Omit<WorkOrder, 'id'>,
  ): Promise<WorkOrder> => {
    const newWorkOrder = await workOrderService.createWorkOrder(data)
    await refreshWorkOrders()
    return newWorkOrder
  }

  const updateWorkOrder = async (
    id: string,
    data: Partial<WorkOrder>,
  ): Promise<WorkOrder> => {
    const updatedWorkOrder = await workOrderService.updateWorkOrder(id, data)
    await refreshWorkOrders()
    return updatedWorkOrder
  }

  const updateWorkOrderStatus = async (
    workOrderId: string | number,
    status: WorkOrderStatus,
    declineReason?: string,
  ): Promise<WorkOrder> => {
    const updatedWorkOrder = await workOrderService.updateWorkOrderStatus(
      workOrderId,
      status,
      declineReason,
    )
    await refreshWorkOrders()
    return updatedWorkOrder
  }

  const assignDrivers = async (
    workOrderId: string | number,
    driverIds: number[],
  ): Promise<DriverAssignmentResponse> => {
    const response = await workOrderService.assignDrivers(
      workOrderId,
      driverIds,
    )
    // Note: We don't refresh work orders here as driver assignment
    // doesn't change the work order itself, just adds drivers to contacts
    return response
  }

  // Fetch work orders on mount (ref guard prevents StrictMode double-fetch)
  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true
      refreshWorkOrders()
    }
  }, [refreshWorkOrders])

  return {
    workOrders,
    isLoading,
    error,
    createWorkOrder,
    updateWorkOrder,
    updateWorkOrderStatus,
    assignDrivers,
    refreshWorkOrders,
  }
}
